import { useQuery, useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback } from 'react';

// ==================== GraphQL Operations ====================

export const GET_USERS = gql`
  query GetUsers {
    getUsers {
      id
      username
      email
      firstName
      lastName
      role
      status
      lastLoginAt
      createdAt
    }
  }
`;

export const GET_USER_BY_ID = gql`
  query GetUserById($id: ID!) {
    getUserById(id: $id) {
      id
      username
      email
      firstName
      lastName
      role
      status
      accesoConfiguracion
      lastLoginAt
      createdAt
    }
  }
`;

export const CREATE_USER = gql`
  mutation CreateUser($input: CreateUserInput!) {
    createUser(input: $input) {
      success
      userId
      message
    }
  }
`;

export const UPDATE_USER = gql`
  mutation UpdateUser($id: ID!, $input: UpdateUserInput!) {
    updateUser(id: $id, input: $input) {
      success
      message
    }
  }
`;

export const DELETE_USER = gql`
  mutation DeleteUser($id: ID!, $deletedBy: String!) {
    deleteUser(id: $id, deletedBy: $deletedBy) {
      success
      message
    }
  }
`;

export const RESET_USER_PASSWORD = gql`
  mutation ResetUserPassword($id: ID!, $newPassword: String!, $resetBy: String!) {
    resetUserPassword(id: $id, newPassword: $newPassword, resetBy: $resetBy) {
      success
      message
    }
  }
`;

// ==================== Types ====================

export type UserRole = 'SUPER_ADMIN' | 'ADMIN' | 'MANAGER' | 'OPERATOR' | 'VIEWER';
export type UserStatus = 'ACTIVE' | 'INACTIVE' | 'PENDING_ACTIVATION' | 'SUSPENDED';

export interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  status: UserStatus;
  accesoConfiguracion?: boolean;
  lastLoginAt: Date | null;
  createdAt: Date;
}

export interface CreateUserInput {
  username: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role?: UserRole;
  createdBy?: string;
}

export interface UpdateUserInput {
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: UserRole;
  status?: UserStatus;
  accesoConfiguracion?: boolean;
  updatedBy?: string;
}

// ==================== Hooks ====================

/**
 * Hook para obtener la lista de usuarios
 */
export const useUsers = () => {
  const { data, loading, error, refetch } = useQuery<{ getUsers: User[] }>(GET_USERS, {
    fetchPolicy: 'cache-and-network',
  });

  return {
    users: data?.getUsers || [],
    loading,
    error,
    refetch,
  };
};

/**
 * Hook para obtener un usuario por ID
 */
export const useUserById = (id: string) => {
  const { data, loading, error, refetch } = useQuery<{ getUserById: User | null }>(
    GET_USER_BY_ID,
    {
      variables: { id },
      skip: !id,
      fetchPolicy: 'cache-and-network',
    }
  );

  return {
    user: data?.getUserById || null,
    loading,
    error,
    refetch,
  };
};

/**
 * Hook para crear un usuario
 */
export const useCreateUser = () => {
  const [createMutation, { loading }] = useMutation(CREATE_USER, {
    refetchQueries: [{ query: GET_USERS }],
  });

  const createUser = useCallback(
    async (input: CreateUserInput) => {
      const result = await createMutation({ variables: { input } });
      return result.data?.createUser;
    },
    [createMutation]
  );

  return {
    createUser,
    creating: loading,
  };
};

/**
 * Hook para actualizar un usuario
 */
export const useUpdateUser = () => {
  const [updateMutation, { loading }] = useMutation(UPDATE_USER, {
    refetchQueries: [{ query: GET_USERS }],
  });

  const updateUser = useCallback(
    async (id: string, input: UpdateUserInput) => {
      const result = await updateMutation({ variables: { id, input } });
      return result.data?.updateUser;
    },
    [updateMutation]
  );

  return {
    updateUser,
    updating: loading,
  };
};

/**
 * Hook para eliminar un usuario
 */
export const useDeleteUser = () => {
  const [deleteMutation, { loading }] = useMutation(DELETE_USER, {
    refetchQueries: [{ query: GET_USERS }],
  });

  const deleteUser = useCallback(
    async (id: string, deletedBy: string) => {
      const result = await deleteMutation({ variables: { id, deletedBy } });
      return result.data?.deleteUser;
    },
    [deleteMutation]
  );

  return {
    deleteUser,
    deleting: loading,
  };
};

/**
 * Hook para resetear contraseña de usuario
 */
export const useResetUserPassword = () => {
  const [resetMutation, { loading }] = useMutation(RESET_USER_PASSWORD);

  const resetPassword = useCallback(
    async (id: string, newPassword: string, resetBy: string) => {
      const result = await resetMutation({ variables: { id, newPassword, resetBy } });
      return result.data?.resetUserPassword;
    },
    [resetMutation]
  );

  return {
    resetPassword,
    resetting: loading,
  };
};

// ==================== Helpers ====================

export const roleLabels: Record<UserRole, string> = {
  SUPER_ADMIN: 'Super Administrador',
  ADMIN: 'Administrador',
  MANAGER: 'Gerente',
  OPERATOR: 'Operador',
  VIEWER: 'Visualizador',
};

export const statusLabels: Record<UserStatus, string> = {
  ACTIVE: 'Activo',
  INACTIVE: 'Inactivo',
  PENDING_ACTIVATION: 'Pendiente',
  SUSPENDED: 'Suspendido',
};

export const roleColors: Record<UserRole, string> = {
  SUPER_ADMIN: '#9333ea',
  ADMIN: '#dc2626',
  MANAGER: '#ea580c',
  OPERATOR: '#2563eb',
  VIEWER: '#6b7280',
};

export const statusColors: Record<UserStatus, string> = {
  ACTIVE: '#16a34a',
  INACTIVE: '#6b7280',
  PENDING_ACTIVATION: '#ca8a04',
  SUSPENDED: '#dc2626',
};
